import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { ProcessFlowService } from '../../services/process-flow.service';
import { ProcessFlowAuditService } from '../../services/process-flow-audit.service';
import { FlowAuditRowComponent } from '../flow-audit-row/flow-audit-row.component';
import Swal from 'sweetalert2';
import {
  actionBadgeClass,
  auditActorLabel,
  auditRangeLabel,
  formatAuditWhen,
  rollbackConfirmHtml,
  rollbackSuccessSwalOptions,
  rollbackSwalOptions,
  stepsChangeLabel
} from '../../utils/flow-audit.utils';

@Component({
  selector: 'app-flow-audit-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    ListCardComponent,
    FlowAuditRowComponent,
    MobileFooterComponent
  ],
  templateUrl: './flow-audit-list.component.html',
  styleUrl: './flow-audit-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class FlowAuditListComponent implements OnInit {
  readonly appSettings = inject(AppSettings);
  private readonly processFlowService = inject(ProcessFlowService);
  private readonly auditService = inject(ProcessFlowAuditService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  processes = signal<any[]>([]);
  rows = signal<any[]>([]);
  total = signal(0);
  loading = signal(false);
  rollingBackId = signal<number | null>(null);
  processFilter = signal<string | null>(null);

  readonly pageSize = 50;
  offset = 0;
  readonly skeletonRows = [1, 2, 3, 4, 5];

  readonly formatWhen = formatAuditWhen;
  readonly badgeClass = actionBadgeClass;
  readonly actorLabel = auditActorLabel;
  readonly stepsLabel = stepsChangeLabel;

  ngOnInit(): void {
    this.readProcessQuery();
    this.loadCatalog();
    this.load();
  }

  private readProcessQuery(): void {
    const q = this.route.snapshot.queryParamMap.get('process');
    if (!q) return;
    this.processFilter.set(q.trim().toUpperCase());
  }

  private loadCatalog(): void {
    this.processFlowService.getCatalog().subscribe({
      next: (list) => this.processes.set(Array.isArray(list) ? list : []),
      error: () => this.processes.set([])
    });
  }

  onFilterChange(value: string | null): void {
    this.processFilter.set(value);
    this.offset = 0;
    this.syncProcessQuery(value);
    this.load();
  }

  clearFilter(): void {
    this.onFilterChange(null);
  }

  private syncProcessQuery(value: string | null): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { process: value || null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  load(): void {
    this.loading.set(true);
    this.auditService.list(this.listParams()).subscribe({
      next: (res) => this.onListOk(res),
      error: () => this.onListFail()
    });
  }

  private listParams() {
    return {
      processSlug: this.processFilter(),
      limit: this.pageSize,
      offset: this.offset
    };
  }

  private onListOk(res: any): void {
    this.rows.set(res?.rows ?? []);
    this.total.set(res?.total ?? 0);
    this.loading.set(false);
  }

  private onListFail(): void {
    this.rows.set([]);
    this.total.set(0);
    this.loading.set(false);
  }

  async rollback(row: any): Promise<void> {
    if (!this.canStartRollback(row)) return;
    const choice = await this.askRollback(row);
    if (choice.isDismissed) return;
    this.executeRollback(row, choice.isDenied);
  }

  private canStartRollback(row: any): boolean {
    return !!row?.canRollback && !this.rollingBackId();
  }

  private askRollback(row: any) {
    const label = row.processName || row.processSlug;
    const when = formatAuditWhen(row.createdAt);
    return Swal.fire(rollbackSwalOptions(rollbackConfirmHtml(label, when)));
  }

  private executeRollback(row: any, applyToOpen: boolean): void {
    this.rollingBackId.set(row.id);
    this.auditService.rollback(row.uuid ?? row.id, { applyToOpen }).subscribe({
      next: (res) => this.onRollbackOk(applyToOpen, res),
      error: (err) => this.onRollbackFail(err)
    });
  }

  private onRollbackOk(applyToOpen: boolean, res: any): void {
    this.rollingBackId.set(null);
    Swal.fire(rollbackSuccessSwalOptions(applyToOpen, res?.affectedCount ?? 0));
    this.load();
  }

  private onRollbackFail(err: any): void {
    this.rollingBackId.set(null);
    const detail = err?.error?.message || err?.message || 'Error interno';
    Swal.fire('Error', 'No se pudo restaurar: ' + detail, 'error');
  }

  canPrev(): boolean {
    return this.offset > 0;
  }

  canNext(): boolean {
    return this.offset + this.pageSize < this.total();
  }

  prevPage(): void {
    if (!this.canPrev()) return;
    this.offset = Math.max(0, this.offset - this.pageSize);
    this.load();
  }

  nextPage(): void {
    if (!this.canNext()) return;
    this.offset += this.pageSize;
    this.load();
  }

  rangeLabel(): string {
    return auditRangeLabel(this.total(), this.offset, this.pageSize);
  }

  isBusy(row: any): boolean {
    return this.rollingBackId() === row?.id;
  }

  anyBusy(): boolean {
    return this.rollingBackId() !== null;
  }
}
